/**
 * Firebase provider factory.
 *
 * The app supports multiple Firebase (FCM) projects. Which one is used is
 * selected at build/runtime via the `VUE_APP_FIREBASE_PROVIDER` env var:
 *   - "KOKATTO" (default, legacy project)
 *   - "QISCUS"  (new project)
 *
 * NOTE: QISCUS currently reuses the KOKATTO values as a placeholder. Replace
 * the QISCUS `config` + `vapidKey` with the real Qiscus project values once
 * the new Firebase project is provisioned.
 */

export interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  // Only present when Realtime Database is enabled on the project. FCM does not
  // use it, so it is optional — a project without RTDB can omit it entirely.
  databaseURL?: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  // Only present when Google Analytics is enabled. FCM does not require it.
  measurementId?: string;
}

export interface FirebaseProvider {
  config: FirebaseConfig;
  vapidKey: string;
}

export type FirebaseProviderName = "KOKATTO" | "QISCUS";

const KOKATTO: FirebaseProvider = {
  config: {
    apiKey: "AIzaSyBVn-Wg7-_P_mfKc4zXBbjYSeA9Lhaqj90",
    authDomain: "kokatto-cs-platform.firebaseapp.com",
    databaseURL: "https://kokatto-cs-platform.firebaseio.com",
    projectId: "kokatto-cs-platform",
    storageBucket: "kokatto-cs-platform.appspot.com",
    messagingSenderId: "214016096672",
    appId: "1:214016096672:web:a778e5e95eaebac2058cc5",
    measurementId: "G-DXEN6HVHWB",
  },
  vapidKey:
    "BM8eXxywknOgVX8VFYdqLg4T37Hiu8UTT3733rR4W28rVfGZelemN_i1udUe6ae6_5e7e66ad9XKL1D90VlZuUw",
};

// TODO(QISCUS): replace with the real Qiscus Firebase project values.
const QISCUS: FirebaseProvider = {
  config: {
    apiKey: "AIzaSyB4YEscmAkfm4_y6G0eiWb9NeVJVKDyyXk",
    authDomain: "kokatto-production-project.firebaseapp.com",
    projectId: "kokatto-production-project",
    storageBucket: "kokatto-production-project.firebasestorage.app",
    messagingSenderId: "745557668392",
    appId: "1:745557668392:web:5cb825db18d80ff221bb1d",
    measurementId: "G-R6SVMMN4YP",
  },
  vapidKey:
    "BJuJnDW377Mw4yTPE9zMbI9b9Rg_C0YDprNnhHWDjiEu1ep-akAqNA3_Gfr6Nyb0TlQkZ7IhlstL1sjAQE4rNzA",
};

const FIREBASE_PROVIDERS: Record<FirebaseProviderName, FirebaseProvider> = {
  KOKATTO,
  QISCUS,
};

const DEFAULT_PROVIDER: FirebaseProviderName = "KOKATTO";

/**
 * Resolves the active provider name from `VUE_APP_FIREBASE_PROVIDER`.
 * Falls back to KOKATTO for any unset/unknown value.
 */
export function getFirebaseProviderName(): FirebaseProviderName {
  const raw = (process.env.VUE_APP_FIREBASE_PROVIDER || "").toUpperCase();
  console.log("FZR Firebase prov", raw);
  return raw in FIREBASE_PROVIDERS
    ? (raw as FirebaseProviderName)
    : DEFAULT_PROVIDER;
}

/** Returns the full provider (config + vapidKey) for the active provider. */
export function getFirebaseProvider(): FirebaseProvider {
  return FIREBASE_PROVIDERS[getFirebaseProviderName()];
}

/** Returns the Firebase app config for the active provider. */
export function getFirebaseConfig(): FirebaseConfig {
  return getFirebaseProvider().config;
}

/** Returns the Web Push (VAPID) key for the active provider. */
export function getFirebaseVapidKey(): string {
  return getFirebaseProvider().vapidKey;
}
